import { Schema } from 'src/utils/form';
import SchemaItem from 'src/paginas/item/schema';
import { FormattedMessage } from 'react-intl';

const originalSchemaObj = {
  ...SchemaItem,
  idIntegracao: Schema.string().label(<FormattedMessage id="idIntegracao" />),
  permitirAlterarCodigo: Schema.string().label(
    <FormattedMessage id="label.permitirAlterarCodigoAutomatico" />
  ),
  area: Schema.string().label(<FormattedMessage id="area" />),
  impactosIndiretos: Schema.array().label(
    <FormattedMessage id="label.itensDeImpactoIndireto" />
  ),
  escopo: Schema.string().label(<FormattedMessage id="escopo" />),
  percentualPrevisto: Schema.string().label(),
  percentualExecutado: Schema.string().label(),
  dataInicioAcao: Schema.string(),
  dataTerminoAcao: Schema.string(),
  dataInicioRealizadoAcao: Schema.string(),
  dataTerminoRealizadoAcao: Schema.string(),
  investimentoPrevisto: Schema.string(),
  investimentoRealizado: Schema.string(),
  bloquearInvestimentoPrevisto: Schema.string(),
  bloquearInvestimentoRealizado: Schema.string(),
  bloquearCusteioPrevisto: Schema.string(),
  bloquearCusteioRealizado: Schema.string(),
  bloquearInsercaoManualPlanoAcao: Schema.string()
    .label('Inserção manual de plano de ação')
    .resource('projeto.bloquearInsercaoManualPlanoAcao'),
  farolInvestimento: Schema.string()
    .label('Farol investimento')
    .resource('projeto.farolInvestimento'),
  ultimoFarol: Schema.string().label('Ultimo farol'),
  custeioPrevisto: Schema.string().label(),
  custeioRealizado: Schema.string().label(),
  fluxoAprovacao: Schema.string().label(),
  tabelaValor: Schema.string().label(<FormattedMessage id="tabelaValores" />),
  equipe: Schema.array().label(),
  anexosStageGateEtapas: Schema.array().label(<FormattedMessage id="anexos" />),
};

const getFieldConfigInCurrentStage = (rootValue, fieldId) => {
  const parentValue = rootValue ?? {};
  if (!parentValue.stageGate) {
    return { utilizar: true, obrigatorio: true };
  }

  const etapa =
    parentValue.etapaAtualStageGate || parentValue.etapaExibidaStageGate;
  if (!etapa) {
    return { utilizar: false, obrigatorio: false };
  }

  let camposEtapa = JSON.parse(etapa.camposEtapaJSON ?? '[]');
  let atributosEtapa = JSON.parse(etapa.atributosEtapaJSON ?? '[]');

  const fieldsConfig = [
    ...(Array.isArray(camposEtapa) ? camposEtapa : []),
    ...(Array.isArray(atributosEtapa) ? atributosEtapa : []),
  ];

  const config = fieldsConfig.find(
    (c) => c?.id != null && String(c.id) === String(fieldId)
  );

  return {
    utilizar: config?.utilizar === true,
    obrigatorio: config?.obrigatorio === true,
  };
};

const applyStageGateConditional = (originalSchemaObj) => {
  const newSchemaObj = { ...originalSchemaObj };

  //Area
  newSchemaObj.area = Schema.string()
    .label(<FormattedMessage id="area" />)
    .required({
      onlyIf: (context) => {
        const rootValue = context.parent.value;
        const { utilizar, obrigatorio } = getFieldConfigInCurrentStage(
          rootValue,
          'area'
        );
        return utilizar && obrigatorio;
      },
    });

  //IdIntegracao
  newSchemaObj.idIntegracao = Schema.string()
    .label(<FormattedMessage id="idIntegracao" />)
    .required({
      onlyIf: (context) => {
        const rootValue = context.parent.value;
        const { utilizar, obrigatorio } = getFieldConfigInCurrentStage(
          rootValue,
          'idIntegracao'
        );
        const fieldRule =
          !context.parent.value.utilizarCodigoAutomatico &&
          context.parent.value.itemTemplate == null;
        return utilizar && obrigatorio && fieldRule;
      },
    });

  //Fluxo aprovação
  newSchemaObj.fluxoAprovacao = Schema.string()
    .label()
    .required({
      onlyIf: (context) => {
        const rootValue = context.parent.value;
        const { utilizar, obrigatorio } = getFieldConfigInCurrentStage(
          rootValue,
          'fluxoAprovacao'
        );
        const fieldRule =
          context.parent.value.configuracaoGeral?.utilizarPatrocinador &&
          context.parent.value.aprovarCadastro;
        return utilizar && obrigatorio && fieldRule;
      },
    });

  //TabelaValores
  newSchemaObj.tabelaValor = Schema.string()
    .label(<FormattedMessage id="tabelaValores" />)
    .required({
      onlyIf: (context) => {
        const rootValue = context.parent.value;
        const { utilizar, obrigatorio } = getFieldConfigInCurrentStage(
          rootValue,
          'tabelaValor'
        );
        const fieldRule = context.parent.value.usarTabelaValores == true;
        return utilizar && obrigatorio && fieldRule;
      },
    });

  //Tags
  newSchemaObj.tags = Schema.array(
    Schema.object({
      id: Schema.number(),
    })
  )
    .label(<FormattedMessage id="tags" />)
    .required({
      onlyIf: (context) => {
        const rootValue = context.parent.value;
        const { utilizar, obrigatorio } = getFieldConfigInCurrentStage(
          rootValue,
          'tags'
        );
        return utilizar && obrigatorio;
      },
    });

  // Campos customizados
  newSchemaObj.camposCustomizados = Schema.array(
    Schema.object({
      campoCustomizadoId: Schema.number(),
      valor: Schema.any().required({
        onlyIf: (context) => {
          const item = context.parent.value;
          const rootValue = context.parent.parent.parent.value;
          const fieldId = item.id;

          const { utilizar, obrigatorio } = getFieldConfigInCurrentStage(
            rootValue,
            fieldId
          );
          if (!utilizar) return false;

          const isMulti =
            item.campo?.tipo?.id === 5 &&
            (item.campo?.multiSelecao === true || false);

          return obrigatorio && !isMulti;
        },
      }),
      campo: Schema.object({
        valores: Schema.array(Schema.any()).required({
          onlyIf: (context) => {
            const campo = context.parent.value;
            const item = context.parent.parent.value;
            const rootValue = context.parent.parent.parent.parent.value;
            const fieldId = item.campoCustomizadoId;

            const { utilizar, obrigatorio } = getFieldConfigInCurrentStage(
              rootValue,
              fieldId
            );
            if (!utilizar) return false;

            const isMulti =
              campo.tipo?.id === 5 && (campo.multiSelecao === true || false);

            return obrigatorio && isMulti;
          },
        }),
        tipo: Schema.object({
          id: Schema.number(),
        }),
        multiSelecao: Schema.boolean(),
      }),
    })
  );

  // Anexos
  newSchemaObj.anexosStageGateEtapas = Schema.array(
    Schema.object({
      anexoStageGateEtapaId: Schema.number(),
    })
  ).addValidator((context) => {
    const rootValue = context.parent.value;

    const etapa =
      rootValue?.etapaAtualStageGate || rootValue?.etapaExibidaStageGate;

    if (!etapa) return;

    let anexosEtapa = [];
    try {
      anexosEtapa = JSON.parse(etapa.anexosEtapaJSON ?? '[]');
    } catch (e) {
      return;
    }

    const requiredAnexoIds = anexosEtapa
      .filter((a) => a.utilizar === true && a.obrigatorio === true)
      .map((a) => a.id);

    if (requiredAnexoIds.length === 0) return;

    const allFiles = Array.isArray(context.value) ? context.value : [];

    const temAnexoObrigatorioFaltando = requiredAnexoIds.some((anexoId) => {
      const arquivosDesteAnexo = allFiles.filter(
        (file) => file.anexoStageGateEtapaId === anexoId
      );
      return arquivosDesteAnexo.length === 0;
    });

    if (temAnexoObrigatorioFaltando) {
      return 'Campo requerido';
    }
  });

  //Outros campos
  Object.keys(newSchemaObj).forEach((fieldKey) => {
    if (
      [
        'area',
        'idIntegracao',
        'fluxoAprovacao',
        'tabelaValor',
        'tags',
        'camposCustomizados',
        'anexosStageGateEtapas',
      ].includes(fieldKey)
    )
      return; // Pular os campos já tratados

    newSchemaObj[fieldKey] = newSchemaObj[fieldKey].required({
      onlyIf: (context) => {
        const rootValue = context.parent.value;
        const { utilizar, obrigatorio } = getFieldConfigInCurrentStage(
          rootValue,
          fieldKey
        );
        return utilizar && obrigatorio;
      },
    });
  });

  return newSchemaObj;
};

export const getStageGateSchema = () =>
  Schema.object(applyStageGateConditional(originalSchemaObj));
